export const acceptableUseContent = `# POLITICA DE USO ACEPTABLE

## PricingIntelligence

---

> **AVISO IMPORTANTE:** Este documento es un modelo referencial y requiere revision obligatoria por un abogado habilitado en el Estado Plurinacional de Bolivia antes de su publicación o uso. No constituye asesoramiento juridico. Los placeholders indicados entre corchetes [ASI] deben ser completados con la información correspondiente antes de su entrada en vigencia.

---

**Responsable:** [NOMBRE DE LA EMPRESA]
**NIT:** [NIT]
**Domicilio Legal:** [DIRECCION], [CIUDAD], Estado Plurinacional de Bolivia

**Fecha de entrada en vigencia:** [FECHA_VIGENCIA]
**Ultima actualizacion:** [FECHA_ACTUALIZACION]

---

## 1. INTRODUCCION Y ALCANCE

**1.1.** La presente Politica de Uso Aceptable (en adelante, la "Politica" o "PUA") establece las reglas y directrices que rigen el uso de la plataforma PricingIntelligence (en adelante, la "Plataforma") por parte de todos los usuarios, Tenants y administradores.

**1.2.** Esta Politica forma parte integrante de los Terminos y Condiciones de Uso y del Contrato de Licencia SaaS de PricingIntelligence. El uso de la Plataforma implica la aceptacion plena de la presente Politica.

**1.3.** El proposito de esta Politica es garantizar un entorno seguro, legal y justo para todos los usuarios de la Plataforma, proteger la integridad del Servicio y prevenir usos que puedan causar dano a terceros, al Proveedor o a otros usuarios.

---

## 2. USO PERMITIDO

**2.1.** La Plataforma esta disenada y autorizada para los siguientes usos:

   a) **Gestion de precios:** Creacion, configuracion y administracion de modelos de calculo de precios para los productos y servicios propios del Tenant;

   b) **Generacion de cotizaciones:** Elaboracion, envio, seguimiento y gestion de cotizaciones comerciales para clientes del Tenant;

   c) **Flujos de aprobacion:** Gestion de procesos internos de aprobacion de cotizaciones y precios;

   d) **Gestion de catalogo:** Administracion de materiales, productos, costos y atributos comerciales propios del Tenant;

   e) **Inteligencia competitiva legal:** Monitoreo de precios publicados en fuentes de acceso publico para analisis comparativo, dentro de los limites legales y eticos;

   f) **Asistencia mediante IA:** Consultas al Asistente de Inteligencia Artificial para obtener orientacion informativa sobre temas de pricing y gestion comercial;

   g) **Generacion de reportes:** Elaboracion de reportes, analisis y documentos basados en los datos propios del Tenant;

   h) **Colaboracion interna:** Uso de la Plataforma por el equipo comercial del Tenant para coordinacion y gestion de actividades de pricing.

**2.2.** El uso de la Plataforma debera realizarse siempre de conformidad con la legislacion boliviana vigente, los presentes Terminos y Condiciones, y los principios de buena fe y lealtad comercial.

---

## 3. PROHIBICIONES GENERALES

**3.1.** Queda expresamente prohibido utilizar la Plataforma para:

### 3.1.1. Actividades Ilicitas

   a) Cualquier actividad que viole la legislacion del Estado Plurinacional de Bolivia o de cualquier otra jurisdiccion aplicable;

   b) Actividades fraudulentas, enganosas o que induzcan a error;

   c) Lavado de dinero, financiamiento del terrorismo o cualquier otra actividad delictiva;

   d) Evasion o elusion de obligaciones tributarias;

   e) Cualquier actividad que constituya delito conforme al Codigo Penal boliviano (Ley 1768), incluyendo los delitos informaticos tipificados en los articulos 363 bis y ter.

### 3.1.2. Practicas Anticompetitivas y Colusion de Precios

   a) **Acuerdos de fijacion de precios:** Utilizar la Plataforma para coordinar, acordar o fijar precios con competidores, directa o indirectamente. Esta prohibicion se fundamenta en el Decreto Supremo 29519 y la normativa de competencia boliviana;

   b) **Division de mercados:** Utilizar la Plataforma para acordar con competidores la division de territorios, clientes o segmentos de mercado;

   c) **Manipulacion de licitaciones:** Utilizar la Plataforma para coordinar ofertas en procesos de licitacion publica o privada;

   d) **Dumping o precios predatorios:** Utilizar la Plataforma para establecer precios por debajo del costo con el proposito de eliminar competidores;

   e) **Intercambio de informacion sensible:** Compartir a traves de la Plataforma informacion comercialmente sensible (precios, costos, margenes, estrategias) con competidores.

### 3.1.3. Uso Abusivo del Monitoreo de Precios (Scraping)

   a) Configurar frecuencias de monitoreo excesivas que puedan causar sobrecarga o dano a los sitios web monitoreados;

   b) Monitorear sitios web que prohiban expresamente el scraping en sus terminos de uso, cuando el usuario tenga conocimiento de dicha prohibicion;

   c) Eludir mecanismos de proteccion tecnica (CAPTCHAs, rate limiting, robots.txt) de sitios web de terceros;

   d) Recopilar datos personales de consumidores o visitantes de sitios web de terceros;

   e) Utilizar los datos recopilados para fines distintos al analisis competitivo propio del Tenant;

   f) Redistribuir, revender o compartir con terceros los datos de precios recopilados;

   g) Utilizar los datos de monitoreo como base para practicas anticompetitivas descritas en la seccion 3.1.2.

### 3.1.4. Ingenieria Inversa y Seguridad

   a) Descompilar, desensamblar, realizar ingenieria inversa o intentar obtener el codigo fuente de la Plataforma;

   b) Intentar eludir, desactivar o interferir con los mecanismos de seguridad, autenticacion o control de acceso de la Plataforma;

   c) Realizar pruebas de penetracion, escaneo de vulnerabilidades o ataques de cualquier naturaleza contra la Plataforma sin autorizacion previa y por escrito del Proveedor;

   d) Utilizar herramientas automatizadas (bots, crawlers, scrapers) para acceder a la Plataforma, salvo las funcionalidades de monitoreo expresamente habilitadas;

   e) Intentar explotar vulnerabilidades conocidas o desconocidas de la Plataforma.

### 3.1.5. Acceso a Datos de Otros Tenants

   a) Intentar acceder, visualizar, copiar, modificar o eliminar datos pertenecientes a otros Tenants;

   b) Intentar escalar privilegios o acceder a funcionalidades o datos no autorizados para el rol del usuario;

   c) Utilizar credenciales de acceso de otro usuario o Tenant sin autorizacion;

   d) Cualquier intento de eludir el aislamiento logico entre Tenants.

### 3.1.6. Almacenamiento de Datos Regulados

   a) Almacenar en la Plataforma datos de salud protegidos (historias clinicas, diagnosticos, tratamientos) que no esten directamente relacionados con la gestion de precios de productos o servicios de salud;

   b) Almacenar datos financieros regulados (datos de tarjetas de credito/debito, numeros de cuentas bancarias, informacion bancaria de terceros) sujetos a normas de proteccion especificas;

   c) Almacenar datos clasificados o sujetos a secreto militar, de seguridad nacional o de inteligencia del Estado;

   d) Almacenar datos personales sensibles de terceros que no sean estrictamente necesarios para la gestion comercial (datos biometricos, orientacion sexual, afiliacion politica, creencias religiosas);

   e) Almacenar informacion protegida por el secreto bancario conforme al articulo 98 de la Ley 393.

### 3.1.7. Otros Usos Prohibidos

   a) Sublicenciar, revender, redistribuir o compartir el acceso a la Plataforma con terceros no autorizados;

   b) Utilizar la Plataforma para enviar comunicaciones no solicitadas (spam), contenido malicioso o software danino;

   c) Sobrecargar intencionalmente los servidores de la Plataforma o realizar ataques de denegacion de servicio (DoS/DDoS);

   d) Utilizar la Plataforma de manera que perjudique la disponibilidad o el rendimiento del Servicio para otros usuarios;

   e) Publicar o transmitir contenido difamatorio, calumnioso, amenazante, obsceno o que incite al odio;

   f) Suplantar la identidad de otra persona o entidad;

   g) Utilizar la Plataforma con fines personales no relacionados con la actividad comercial del Tenant.

---

## 4. USO DEL ASISTENTE DE INTELIGENCIA ARTIFICIAL

**4.1.** Al utilizar el Asistente de IA, el Usuario debera:

   a) Formular consultas relacionadas con la gestion comercial y de precios;

   b) No intentar manipular, engañar o desviar el proposito del Asistente de IA;

   c) No utilizar el Asistente de IA para generar contenido ilicito, ofensivo, difamatorio o danino;

   d) No incluir datos personales sensibles innecesarios en las consultas;

   e) Verificar de forma independiente toda informacion proporcionada por el Asistente antes de tomar decisiones;

   f) No intentar extraer los prompts de sistema o la configuracion interna del Asistente de IA.

**4.2.** El Proveedor se reserva el derecho de limitar o suspender el acceso al Asistente de IA en caso de uso abusivo o no conforme con la presente Politica.

---

## 5. LIMITES DE USO

**5.1.** El uso de la Plataforma esta sujeto a los limites establecidos en el Plan contratado, incluyendo pero no limitado a: numero de usuarios, numero de modelos de precios, volumen de cotizaciones, frecuencia de monitoreo de precios y volumen de consultas al Asistente de IA.

**5.2.** El uso que exceda sistematicamente los limites del Plan contratado podra dar lugar a:

   a) Notificacion al Administrador del Tenant sobre el exceso de uso;
   b) Recomendacion de actualizacion a un Plan superior;
   c) Limitacion temporal de funcionalidades hasta la actualizacion del Plan;
   d) Facturacion adicional por el uso excedente, previa notificacion y conforme a las tarifas vigentes.

---

## 6. CONSECUENCIAS POR VIOLACION DE LA POLITICA

**6.1.** El incumplimiento de la presente Politica podra dar lugar a las siguientes acciones, de forma proporcional a la gravedad de la violacion:

### 6.1.1. Violaciones Leves

*Ejemplos: exceso moderado de limites de uso, consultas inadecuadas al Asistente de IA, configuracion excesiva de frecuencia de monitoreo.*

   a) Notificacion escrita al Usuario y al Administrador del Tenant;
   b) Solicitud de correccion de la conducta;
   c) Orientacion sobre el uso adecuado de la Plataforma.

### 6.1.2. Violaciones Moderadas

*Ejemplos: intentos fallidos de acceso a datos de otros Tenants, almacenamiento de datos regulados, uso reiterado tras advertencia por violacion leve, sublicencia no autorizada.*

   a) Suspension temporal del acceso del Usuario responsable (de 24 horas a 30 dias, segun la gravedad);
   b) Eliminacion del contenido o datos que violen la Politica;
   c) Restriccion temporal de funcionalidades especificas;
   d) Notificacion formal al Administrador del Tenant con plazo para adoptar medidas correctivas.

### 6.1.3. Violaciones Graves

*Ejemplos: acceso efectivo a datos de otros Tenants, uso para colusion de precios o actividades ilicitas, ataques contra la infraestructura de la Plataforma, ingenieria inversa, brecha de seguridad causada intencionalmente.*

   a) Suspension inmediata y definitiva del acceso del Usuario y, en su caso, del Tenant;
   b) Terminacion del Contrato con causa, sin derecho a reembolso;
   c) Eliminacion de los datos del Tenant infractor (previa oportunidad de exportacion cuando sea aplicable);
   d) Reporte a las autoridades competentes cuando la conducta pueda constituir delito;
   e) Ejercicio de las acciones legales civiles y/o penales que correspondan conforme a la legislacion boliviana.

**6.2.** El Proveedor se reserva el derecho de determinar la gravedad de la violacion y la medida aplicable, considerando las circunstancias de cada caso, la reincidencia y el impacto de la conducta.

---

## 7. PROCESO DE INVESTIGACION Y NOTIFICACION

**7.1. Deteccion.** El Proveedor podra detectar posibles violaciones de la presente Politica mediante:

   a) Sistemas automatizados de monitoreo y deteccion de anomalias;
   b) Reportes de otros usuarios de la Plataforma;
   c) Reportes de terceros afectados;
   d) Auditorias internas periodicas;
   e) Notificaciones de autoridades competentes.

**7.2. Investigacion.** Ante la deteccion de una posible violacion, el Proveedor seguira el siguiente procedimiento:

   a) **Evaluacion preliminar:** Analisis inicial de la informacion disponible para determinar si existe indicios razonables de una violacion. Plazo: cuarenta y ocho (48) horas desde la deteccion;

   b) **Medidas cautelares:** Si la evaluacion preliminar evidencia un riesgo inmediato para la seguridad de la Plataforma, los datos de otros usuarios o la integridad del Servicio, el Proveedor podra adoptar medidas cautelares (suspension temporal del acceso) de forma inmediata y sin previo aviso;

   c) **Investigacion formal:** Analisis detallado de los hechos, revision de logs de actividad, y recopilacion de evidencia. Plazo: hasta quince (15) dias habiles;

   d) **Audiencia:** El Usuario o Tenant investigado sera notificado de los hallazgos y tendra la oportunidad de presentar sus descargos y alegatos dentro de los cinco (5) dias habiles siguientes a la notificacion;

   e) **Resolucion:** El Proveedor emitira una resolucion motivada dentro de los diez (10) dias habiles siguientes a la recepcion de los descargos o al vencimiento del plazo para presentarlos.

**7.3. Notificacion.** Las notificaciones relacionadas con la investigacion se realizaran:

   a) Al correo electronico del Usuario investigado;
   b) Al correo electronico del Administrador del Tenant;
   c) Mediante aviso en la Plataforma (cuando el acceso no haya sido suspendido);
   d) Por correo certificado al domicilio legal del Tenant, en caso de violaciones graves.

**7.4. Confidencialidad.** Las investigaciones se llevaran a cabo de forma confidencial. El Proveedor no divulgara la identidad del denunciante (cuando aplique) ni los detalles de la investigacion a terceros, salvo requerimiento de autoridad competente.

**7.5. Apelacion.** El Usuario o Tenant afectado por una resolucion podra presentar una apelacion por escrito dentro de los diez (10) dias habiles siguientes a la notificacion de la resolucion. La apelacion sera resuelta en un plazo maximo de quince (15) dias habiles.

---

## 8. REPORTE DE VIOLACIONES

**8.1.** Cualquier usuario de la Plataforma o tercero que detecte una posible violacion de la presente Politica podra reportarla a traves de:

   - **Correo electronico:** [EMAIL_REPORTES_ABUSO]
   - **Formulario web:** [URL_FORMULARIO_REPORTE]

**8.2.** El reporte debera incluir:

   a) Descripcion de la conducta observada;
   b) Fecha y hora aproximada de los hechos;
   c) Evidencia disponible (capturas de pantalla, URLs, etc.);
   d) Datos de contacto del denunciante (opcionales, pero recomendados para seguimiento).

**8.3.** El Proveedor acusara recibo del reporte dentro de las cuarenta y ocho (48) horas habiles siguientes e informara al denunciante sobre el resultado de la investigacion cuando sea apropiado y el denunciante haya proporcionado datos de contacto.

---

## 9. COLABORACION CON AUTORIDADES

**9.1.** El Proveedor cooperara con las autoridades bolivianas competentes en la investigacion de actividades ilicitas realizadas a traves de la Plataforma, conforme a la legislacion vigente.

**9.2.** La cooperacion podra incluir la provision de informacion, datos y registros de actividad del Usuario o Tenant investigado, siempre que exista un requerimiento formal y legalmente vinculante de la autoridad competente.

**9.3.** Cuando la legislacion lo permita, el Proveedor notificara al Usuario o Tenant afectado sobre el requerimiento de la autoridad.

---

## 10. ACTUALIZACIONES DE LA POLITICA

**10.1.** El Proveedor se reserva el derecho de modificar la presente Politica en cualquier momento. Las modificaciones seran notificadas a los Usuarios con al menos quince (15) dias de anticipacion a su entrada en vigencia, mediante correo electronico y aviso en la Plataforma.

**10.2.** El uso continuado de la Plataforma tras la entrada en vigencia de las modificaciones se considerara aceptacion de las mismas.

---

**[NOMBRE DE LA EMPRESA]**
**NIT: [NIT]**
**[DIRECCION], [CIUDAD]**
**Estado Plurinacional de Bolivia**

*Documento vigente desde [FECHA_VIGENCIA].*
*Version: 1.0*`;
